import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import {
  getActiveTab,
  getCurrentUserId,
  getIsInitialUqualReferral,
  getNhDealApplicationId,
  getProspectId,
  getSelfReferralInSession,
  getUserRole,
} from "features/authSlice";
import { RegistrationRoleType } from "pages/registration/registrationService";
import { selectIsUqualAdBannerDismissed, setUqualAdBannerDismissed } from "features/uqualAdBannerSlice";
import { useGetDashboardItemsQuery, useCheckCreditScoreQuery } from "pages/dashboard/home/dashboardService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { UQUAL_AD_BANNER_DISMISS_KEY, UQUAL_AD_FIRST_SESSION_KEY } from "utilities/Constant";

export const useUqualAdBannerVisibility = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const userId = useSelector(getCurrentUserId);
  const activeTab = useSelector(getActiveTab);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const prospectId = useSelector(getProspectId);
  const selfReferralInSession = useSelector(getSelfReferralInSession);
  const isInitialUqualReferral = useSelector(getIsInitialUqualReferral);
  const bannerDismissed = useSelector(selectIsUqualAdBannerDismissed);
  const userRole = useSelector(getUserRole);
  const userRoleNormalized = (userRole || "").toLowerCase();

  const isOnDashboardHome = useMemo(() => {
    const onDashboardRoot = pathname === "/dashboard" || pathname === "/dashboard/";
    const isHomeTab = activeTab === "" || activeTab === NavigationIds.Home;
    return onDashboardRoot && isHomeTab;
  }, [pathname, activeTab]);

  const { data: featureFlag } = useGetFeatureFlagsQuery();

  const { data: dashboardData } = useGetDashboardItemsQuery(
    { dealApplicationId: nhDealApplicationId, userId: userId ?? "" },
    { skip: !isOnDashboardHome || !nhDealApplicationId || !userId },
  );

  const { data: isCreditScoreLow } = useCheckCreditScoreQuery(
    { nhDealApplicationId, prospectId },
    { skip: !isOnDashboardHome || !nhDealApplicationId || !prospectId },
  );

  const isUqualSelfReferralDone = dashboardData?.isUqualSelfReferralDone;
  const isFlagOn = Boolean(featureFlag?.uQualSelfReferralAdLogicEnabled);

  const firstSessionKey = `${UQUAL_AD_FIRST_SESSION_KEY}_${userId ?? ""}`;

  // Persist first-session "new" status so refresh does not hide the ad after backend flips isNew to false.
  const wasNewOnLoadRef = useRef<boolean>(sessionStorage.getItem(firstSessionKey) === "true");

  useEffect(() => {
    wasNewOnLoadRef.current = sessionStorage.getItem(firstSessionKey) === "true";
  }, [firstSessionKey]);

  useEffect(() => {
    if (!isOnDashboardHome
      || dashboardData == null || dashboardData == undefined
      || wasNewOnLoadRef == null) return;

    const isSpousalCoBorrower = userRoleNormalized === RegistrationRoleType.SpousalCoBorrower.toLowerCase();
    const isNewFromDashboard = isSpousalCoBorrower ? Boolean(dashboardData.isCoborrowerNew) : Boolean(dashboardData.isNew);

    if (isNewFromDashboard && !wasNewOnLoadRef.current) {
      wasNewOnLoadRef.current = true;
      sessionStorage.setItem(firstSessionKey, "true");
    }
  }, [isOnDashboardHome, dashboardData, userRoleNormalized, firstSessionKey]);

  const isNew = wasNewOnLoadRef.current;

  const isEligible = useMemo((): boolean => {
    if (!isCreditScoreLow || !isFlagOn || !isNew) return false;
    if (selfReferralInSession) return isInitialUqualReferral;
    return !isUqualSelfReferralDone;
  }, [isCreditScoreLow, isFlagOn, isNew, selfReferralInSession, isInitialUqualReferral, isUqualSelfReferralDone]);

  const isBannerVisible = useMemo(() => isOnDashboardHome && isEligible && !bannerDismissed, [isOnDashboardHome, isEligible, bannerDismissed]);

  const dismissBanner = useCallback(() => {
    sessionStorage.setItem(UQUAL_AD_BANNER_DISMISS_KEY, "true");
    dispatch(setUqualAdBannerDismissed(true));
  }, [dispatch]);

  return { isBannerVisible, dismissBanner };
};
